import { Modules } from "@/modules/module-list";
import { getMenu } from "../wp/getMenu";
import { GetStaticPropsContext } from "next";
import { CategoryType, MenuType, OptionsType } from "../types/baseTypes";
import { globalGetStaticPaths } from "../wp/globalGetStaticPaths";
import { globalGetPageData } from "../wp/globalGetPageData";
import { HeadMeta } from "../components/Header/Head";
import { getRelativePathFromLink, isValidWpSlug } from "../utils/url";
import { hasChildPages } from "../wp/hasChildren";
import {
  getFooterOptions,
  getHeadersOptions,
  getIconsOptions,
  getModuleOptions,
} from "../getData/getModuleOptions";
import {
  ModuleOptionsType,
  HeadersOptionsType,
  StartPageType,
  IconsType,
  popupOptionType,
} from "@/types/ModuleOptionsType";
import { filterObjectByArrayKeys } from "../utils/filterObjectByArrayKeys";
import { getThemeOptions } from "../wp/getOptions";
import {
  LabelsType,
  NewsLetterOptionType,
  OptionsEndCardsType,
  OptionsFooterType,
  OptionsGoogleMapType,
  OptionsHeaderType,
  OptionsSearchType,
  OptionsSecurityHeader,
  OptionsSeoSettingsType,
  OptionsSocialMediaType,
  popupType,
  TemplateRoutingSettings,
} from "@/types/OptionType";
import { Footer } from "../components/Footer/Footer";
import { Header } from "../components/Header/Header/Header";
import { Navbar, NavbarOptionType } from "../components/Navbar/Navbar";
import { getInsta } from "../wp/getInsta";
import { ParentsType } from "../wp/getParentPages";
import { SearchResult } from "../components/SearchResult/SearchResult";
import { GlobalContextProvider } from "@aleksundshantu.com/aunds-utils-react/contexts";
import MotionPopUp from "../components/PopUp/PopUp";

import {
  getIndexedPostSliderData,
  IndexedPostSlidereData,
} from "../getData/getIndexedPostSliderData";
import { useRef } from "react";
import type { Module as ModuleType } from "../wp/types/acf-module-types";
import { Section } from "@/components/Section/Section";
import router from "next/router";
import autoLinkGlossarTerms from "@/utils/glossary/glossaryLinking";
import GlossarTooltip from "@/components/GlossarTooltip/GlossarTooltip.tsx";
import { EphemeralStore, EphemeralStoreKey } from "@/utils/ephemeralStore.ts";

import { useAppContext } from "@/context/AppContext";
import { Post, PostTreeItem } from "@/wp/types/client";
import {
  getIndexedFormsData,
  IndexedFormsData,
} from "@/getData/getIndexedFormsData";
import {
  BreakpointsOptionsType,
  getBreakpointsFromOptions,
} from "@/getData/getBreakpoints";
import {
  getTeaserSliderData,
  TeaserSliderDataType,
} from "@/getData/getTeaserSliderData";
import {
  getFullWidthSliderData,
  MultipleFullWidthSliderDataType,
} from "@/getData/getFullWidthSliderData";
import {
  getQuotesSliderData,
  QuotesSliderDataType,
} from "@/getData/getQuotesSliderData";
import {
  getLogoSliderData,
  LogoSliderDataType,
} from "@/getData/getLogoSliderData";
import {
  getIndexedEventSliderData,
  IndexedEventSliderData,
} from "@/getData/getIndexedEventSliderData";
import { Modal } from "@/modules/Insta/Modal/Modal";
import { Button } from "@/components/Button/Button";
import { getTemplateRouting } from "@/wp/getTemplateRouting";
import {
  getIndexedNewsletterData,
  IndexedNewsletterData,
} from "@/getData/getIndexedNewsletterData";

type Module = ModuleType["module"][number];

interface DynamicPageProps {
  locale: string;
  pageData: Post;
  legalMenu: MenuType;
  mainMenu: MenuType;
  // TODO: Never pass full options object to page props
  // options: OptionsType;
  categories: CategoryType[];
  headerOptions: HeadersOptionsType;
  moduleOptions: ModuleOptionsType;
  socialMedia: OptionsSocialMediaType;
  footerOptions: OptionsFooterType;
  breakpoints: BreakpointsOptionsType | null;
  navbarOption: NavbarOptionType;
  labels: LabelsType;
  footerMenu: MenuType;
  startPageOptions: StartPageType;
  insta: string;
  parents: ParentsType;
  allTeaserData: TeaserSliderDataType[];
  allFullWidthSliderData: MultipleFullWidthSliderDataType[];
  newsletterOptions: NewsLetterOptionType;
  icons: IconsType;
  searchOptions: OptionsSearchType;
  allQuotesData: QuotesSliderDataType[];
  allLogosData: LogoSliderDataType[];
  popupOptions: popupOptionType;
  popupData: popupType;
  endCards: OptionsEndCardsType;
  indexedEventSliderData: IndexedEventSliderData;
  indexedPostSliderData: IndexedPostSlidereData;
  indexedNewsletterData: IndexedNewsletterData;
  headerThemeOptions: OptionsHeaderType;
  securityHeaderOptions: OptionsSecurityHeader;
  googleMapThemeOptions: OptionsGoogleMapType;
  seoSettingsOptions: OptionsSeoSettingsType;
  indexedFormsData: IndexedFormsData;
  formBulderOptions: OptionsType["acf"]["form_builder"];
  routing: TemplateRoutingSettings;
}

export default function DynamicPage({
  // TODO: Never pass full options object to page props
  locale,
  pageData,
  headerOptions,
  moduleOptions,
  socialMedia,
  footerOptions,
  breakpoints,
  mainMenu,
  navbarOption,
  labels,
  footerMenu,
  legalMenu,
  startPageOptions,
  insta,
  parents,
  allTeaserData,
  allFullWidthSliderData,
  allQuotesData,
  newsletterOptions,
  icons,
  searchOptions,
  allLogosData,
  popupOptions,
  popupData,
  endCards,
  indexedEventSliderData,
  indexedPostSliderData,
  headerThemeOptions,
  securityHeaderOptions,
  googleMapThemeOptions,
  indexedFormsData,
  formBulderOptions,
  seoSettingsOptions,
  indexedNewsletterData,
  routing,
}: DynamicPageProps) {
  const bgColorSearchResults =
    "header_seite" in pageData.acf
      ? pageData?.acf?.header_seite?.breadcrumb_bg_color
      : undefined;
  const popupRef = useRef<HTMLDivElement>(null);
  const mainstartAriaLabel = labels?.find(
    (label) => label.key === "aria_label_mainstart",
  );
  const mainstartLabel = labels?.find((label) => label.key === "skip_nav");

  const isBigParagraph =
    "header_seite" in pageData.acf
      ? pageData.acf?.header_seite?.big_paragraph
      : false;
  const { instagramModal } = useAppContext();

  return (
    <GlobalContextProvider iconsRaw={icons} labelsRaw={labels}>
      <HeadMeta
        post={pageData}
        securityHeaderOptions={securityHeaderOptions}
        seoSettingsOptions={seoSettingsOptions}
        routing={routing}
      />
      <div id="pagestart" tabIndex={-1} />
      {/* eslint-disable-next-line react/forbid-elements */}
      <a
        className="skipnav"
        href="#maincontent"
        {...(mainstartAriaLabel && {
          "aria-label": mainstartAriaLabel.value,
        })}
      >
        {mainstartLabel ? mainstartLabel.value : "Navigation überspringen"}
      </a>
      <Navbar
        searchPageSlug={searchOptions?.search_page_link || ""}
        menu={mainMenu}
        socialMedia={socialMedia.platforms}
        navbarOption={navbarOption}
        pageData={pageData}
        isHome={"header_startseite" in pageData.acf}
      />

      {pageData && (
        <>
          <div id="maincontent" />
          <main id={`main-tag`}>
            <Header
              locale={locale}
              startPageOptions={startPageOptions}
              post={pageData}
              headerOptions={headerOptions}
              parents={parents}
              options={headerThemeOptions}
              socialMediaThemeOptions={socialMedia}
              navbarHasBackground={
                navbarOption?.moduleOption?.header_menu?.show_background &&
                "header_startseite" in pageData.acf
              }
            />

            {pageData.acf?.module &&
              (pageData.acf.module as Array<Module>).map((module, index) => {
                const teaserSliderData = allTeaserData.find(
                  (d) => d.index === index,
                );
                const fullWidthSliderData = allFullWidthSliderData.find(
                  (d) => d.index === index,
                )?.data;
                const quotesSliderData = allQuotesData.find(
                  (d) => d.index === index,
                );

                const logoElements = allLogosData.find(
                  (d) => d.index === index,
                );

                return (
                  <Modules
                    index={index}
                    key={`${pageData.slug}-${module.acf_fc_layout}-${index}`}
                    module={module}
                    moduleOptions={moduleOptions}
                    breakpoints={breakpoints}
                    insta={insta}
                    socialMedia={socialMedia}
                    endCards={endCards}
                    teaserSliderData={
                      teaserSliderData ? teaserSliderData.data : []
                    }
                    quotesSliderData={
                      quotesSliderData ? quotesSliderData.data : []
                    }
                    fullWidthSliderData={fullWidthSliderData}
                    newsletterOptions={newsletterOptions}
                    logoElements={logoElements ? logoElements.data : []}
                    indexedEventSliderData={indexedEventSliderData}
                    indexedPostSliderData={indexedPostSliderData}
                    indexedNewsletterData={indexedNewsletterData}
                    isBigParagraph={isBigParagraph}
                    fallbackImages={headerThemeOptions.fallback_images}
                    headerOptions={headerOptions}
                    googleMapThemeOptions={googleMapThemeOptions}
                    type={pageData.type}
                    pageId={pageData.id}
                    indexedFormsData={indexedFormsData}
                    formBulderOptions={formBulderOptions}
                  />
                );
              })}
            {pageData.type !== "page" && (
              <>
                <Section
                  className="section is-gapless-top"
                  columnsClassName={"is-centered"}
                >
                  <div className="column is-10-tablet is-8-widescreen is-centered">
                    <Button
                      type={"secondary"}
                      href={""}
                      target={"_self"}
                      title={
                        labels?.find((label) => label.key === "back")?.value ||
                        ""
                      }
                      onClick={() => router.back()}
                      isBack
                    >
                      <span>
                        {labels?.find((label) => label.key === "back")?.value ||
                          ""}
                      </span>
                    </Button>
                  </div>
                </Section>
              </>
            )}
            <SearchResult
              searchOptions={searchOptions}
              bgColor={bgColorSearchResults || ""}
            />
            <MotionPopUp
              popupOptions={popupOptions}
              popupData={popupData}
              ref={popupRef}
            />
            {instagramModal && <Modal />}
          </main>
        </>
      )}
      <Footer
        autoShowConsentManagement={
          "header_seite" in pageData.acf
            ? pageData.acf?.header_seite?.show_consent_management
            : undefined
        }
        footerOptions={footerOptions}
        socialMedia={socialMedia}
        footerMenu={footerMenu}
        legalMenu={legalMenu}
      />
      <GlossarTooltip key={pageData.slug} />
    </GlobalContextProvider>
  );
}

export async function getStaticPaths({}) {
  const allPaths = await globalGetStaticPaths();
  return {
    paths: allPaths,
    fallback: "blocking",
  };
}

export const isEmptyParentPage = async ({
  pageData,
  locale,
  defaultLocale,
}: {
  pageData: Post;
  locale: string;
  defaultLocale: string;
}): Promise<boolean> => {
  if (!pageData?.acf?.module && pageData.type === "page") {
    const hasChildren = await hasChildPages({
      id: pageData.id,
      locale,
      defaultLocale,
    });
    if (hasChildren) {
      return true;
    }
    return false;
  }
  return false;
};

export async function getStaticProps(context: GetStaticPropsContext) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any --- FIXME: disabled temporarily on migrate to TS.
    const { dynamicRoute }: any = context.params;
    const locale = context.locale || "de";
    const defaultLocale = context.defaultLocale || "de";

    const slug = dynamicRoute?.[dynamicRoute.length - 1];
    if (slug !== undefined && !isValidWpSlug(slug)) {
      return {
        notFound: true,
        revalidate: parseInt(process.env.REVALIDATE_AFTER || "120"),
      };
    }
    if (context.preview && typeof context.previewData !== "string") {
      return {
        notFound: true,
      };
    }

    const privatePageData = EphemeralStore.getEntry(
      context.previewData as EphemeralStoreKey,
    );
    let pageData =
      context.preview && !privatePageData.isNone
        ? privatePageData.value
        : await globalGetPageData({ dynamicRoute, locale, defaultLocale });

    let constructedPagePath, pagePath;

    if (dynamicRoute) {
      pagePath = getRelativePathFromLink(pageData?.link || "");
      constructedPagePath = `/${
        locale === defaultLocale ? "" : locale + "/"
      }${dynamicRoute.join("/")}/`;
    }

    if (!pageData || pagePath !== constructedPagePath) {
      return {
        notFound: true,
        revalidate: parseInt(process.env.REVALIDATE_AFTER || "120"),
      };
    }
    if (pageData.type === "videos" || pageData.type === "audios") {
      const hasSingleView =
        pageData.type === "videos"
          ? pageData.acf?.header_videos.has_single_page
          : pageData.acf?.header_audios.has_single_page;
      if (!hasSingleView) {
        return {
          notFound: true,
          revalidate: parseInt(process.env.REVALIDATE_AFTER || "120"),
        };
      }
    }
    // Return 404 for empty parent pages
    if (await isEmptyParentPage({ pageData, locale, defaultLocale })) {
      return {
        notFound: true,
        revalidate: parseInt(process.env.REVALIDATE_AFTER || "120"),
      };
    }
    const fetchRouting = getTemplateRouting();
    const fetchOptions = getThemeOptions({ locale, defaultLocale });
    /*const fetchCategories = getCategories({
      category: "categories",
      locale: context.locale,
      defaultLocale: context.defaultLocale,
    });*/

    const fetchLegalMenu = getMenu({
      slug: context.locale ? "legal-" + context.locale : "legal",
    });
    const fetchFooterMenu = getMenu({
      slug: context.locale ? "footer-" + context.locale : "footer",
    });
    const fetchMainMenu = getMenu({
      slug: context.locale ? "main-" + context.locale : "main",
    });

    // Do not fetch all module options here. Fetch only the ones needed for the page by using _fields parameter
    const fetchModuleAllOptions = getModuleOptions();
    const fetchHeadersAllOptions = getHeadersOptions();
    const fetchFooterAllOptions = getFooterOptions();
    const fetchIconsAllOptions = getIconsOptions();
    const fetchFullWidthSliderData = getFullWidthSliderData({
      pageData,
      locale,
      defaultLocale,
    });

    const fetchTeaserData = getTeaserSliderData(
      pageData,
      locale,
      defaultLocale,
    );
    const fetchQuotesData = getQuotesSliderData(
      pageData,
      locale,
      defaultLocale,
    );
    const fetchLogosData = getLogoSliderData(pageData, locale, defaultLocale);

    const parents: ParentsType = [];
    if (pageData.type === "page") {
      pageData.post_tree?.parents?.map?.((parent: PostTreeItem) => {
        parents.push({
          title: parent.breadcrumb_title || parent.title,
          url: parent.link,
          isEmptyParentPage: false,
        });
      });
    } else {
      pageData.post_tree?.post_type_root_parents?.map((child: PostTreeItem) => {
        parents.push({
          title: child.breadcrumb_title || child.title,
          url: child.link,
          isEmptyParentPage: false,
        });
      });
      if (pageData.post_tree?.post_type_root) {
        const parent = pageData.post_tree.post_type_root;
        parents.push({
          title: parent.breadcrumb_title || parent.title,
          url: parent.link,
          isEmptyParentPage: false,
        });
      }
    }

    /* const fetchParents = getParentPages({
      nextParent: pageData.parent,
      locale,
      defaultLocale,
    });*/

    const moduleAllOptions = await fetchModuleAllOptions; //first fetch all module options outside the Promise.all to allow other fetches inside Promise.all to use module options
    const themeOptions = await fetchOptions; // first fetch theme options outside the Promise.all to allow other fetches inside Promise.all to use theme options

    const fetchIndexedEventSliderData = getIndexedEventSliderData({
      pageData,
      eventsSliderOptions: moduleAllOptions?.events_slider,
      locale,
      defaultLocale,
    });
    const fetchIndexedPostSliderData = getIndexedPostSliderData({
      pageData,
      locale,
      defaultLocale,
      fallback_options:
        moduleAllOptions?.slider?.slider_options.fallback_options,
      fallback_image: themeOptions?.header.fallback_images.header_posts,
    });

    const fetchIndexedFormsData = getIndexedFormsData({
      pageData,
      locale,
      defaultLocale,
    });
    const fetchIndexedNewsletterData = getIndexedNewsletterData({
      pageData,
      newsletterOptions: themeOptions.newsletter,
      locale,
      defaultLocale,
    });

    const [
      legalMenu,
      mainMenu,
      headersAllOptions,
      footerMenu,
      footerAllOptions,
      iconsAllOptions,
      allTeaserData,
      allFullWidthSliderData,
      allQuotesData,
      allLogosData,
      indexedEventSliderData,
      indexedPostSliderData,
      indexedFormsData,
      indexedNewsletterData,
      routing,
    ] = await Promise.all([
      fetchLegalMenu,
      fetchMainMenu,
      fetchHeadersAllOptions,
      fetchFooterMenu,
      fetchFooterAllOptions,
      fetchIconsAllOptions,
      fetchTeaserData,
      fetchFullWidthSliderData,
      fetchQuotesData,
      fetchLogosData,
      fetchIndexedEventSliderData,
      fetchIndexedPostSliderData,
      fetchIndexedFormsData,
      fetchIndexedNewsletterData,
      fetchRouting,
    ]);

    /*
    //check if we have a "parent"/overview page for the post type
    const postTypePath = customPostTypesPathMapping.find(
      (t) => t.postType === pageData?.type && t.language === locale,
    )?.path;
    if (postTypePath) {
      const postTypeOverviewPage = (
        await getPostForType({
          slug: postTypePath,
          type: "pages",
          defaultLocale,
          locale,
          fields: "acf,link,title",
        })
      )?.[0];
      if (postTypeOverviewPage && "header_seite" in postTypeOverviewPage.acf) {
        const headerTitle = postTypeOverviewPage?.acf?.header_seite?.title;
        parents.unshift({
          isEmptyParentPage: false,
          title: headerTitle ?? "",
          url: postTypeOverviewPage?.link,
        });
      }
    }
  */
    // Get a set of all layouts used on the current page
    const uniqueModuleKeys = new Set(
      (pageData.acf?.module as Array<Module>)?.map?.(
        (module) => module?.acf_fc_layout,
      ),
    );

    let breakpoints = null;
    if (
      uniqueModuleKeys.has("textmedia") ||
      uniqueModuleKeys.has("quotes_banner")
    ) {
      breakpoints = await getBreakpointsFromOptions();
    }

    let uniqueModuleKeysArray: string[] = Array.from(uniqueModuleKeys);

    // it uniqueModuleKeysArray has newsletter thre replace it with newsletter_options
    if (uniqueModuleKeysArray.includes("newsletter")) {
      uniqueModuleKeysArray = uniqueModuleKeysArray.map((key) =>
        key === "newsletter" ? "newsletter_options" : key,
      );
    }

    let moduleOptionsForCurrentPage;
    if (moduleAllOptions) {
      // Get all module options for the current page --- DO NOT pass all module options to the page
      moduleOptionsForCurrentPage = filterObjectByArrayKeys(
        moduleAllOptions,
        uniqueModuleKeysArray,
      );
    }

    const glossarHeaderOptions = headersAllOptions?.header_glossar;
    if (glossarHeaderOptions) {
      if (glossarHeaderOptions.posttypes_to_link.length > 0) {
        if (glossarHeaderOptions.posttypes_to_link.includes(pageData.type)) {
          pageData = await autoLinkGlossarTerms({
            pageData,
            locale,
            defaultLocale,
            showTooltip: glossarHeaderOptions.show_tooltip,
          });
        }
      } else {
        pageData = await autoLinkGlossarTerms({
          pageData,
          locale,
          defaultLocale,
          showTooltip: glossarHeaderOptions.show_tooltip,
        });
      }
    }

    const popupOptions = moduleAllOptions?.popup;

    const instaFeedId = themeOptions.social_media.instagram_feed_id;
    const insta = await getInsta(instaFeedId || "1");
    return {
      props: {
        locale,
        pageData,
        legalMenu,
        footerMenu,
        mainMenu,
        headerOptions: headersAllOptions,
        moduleOptions: moduleOptionsForCurrentPage,
        socialMedia: themeOptions.social_media,
        footerOptions: { ...themeOptions.footer, ...footerAllOptions },
        endCards: themeOptions.end_cards,
        breakpoints,
        navbarOption: {
          themeOption: themeOptions.header,
          moduleOption: moduleAllOptions?.navigation,
        },
        labels: themeOptions.labels || [],
        icons: iconsAllOptions,
        startPageOptions: !dynamicRoute ? moduleAllOptions?.startpage : null,
        insta,
        parents,
        allTeaserData,
        allFullWidthSliderData,
        allQuotesData,
        newsletterOptions: themeOptions.newsletter,
        searchOptions: themeOptions.search,
        allLogosData,
        popupOptions,
        popupData: themeOptions.pop_up,
        indexedEventSliderData,
        indexedPostSliderData,
        indexedNewsletterData,
        headerThemeOptions: themeOptions.header,
        securityHeaderOptions: themeOptions.security_header,
        googleMapThemeOptions: themeOptions.google_maps,
        formBulderOptions: themeOptions?.form_builder,
        seoSettingsOptions: themeOptions?.seo_settings || {},
        indexedFormsData,
        routing,
      },
      revalidate: parseInt(process.env.REVALIDATE_AFTER || "120"),
    };
  } catch (error) {
    console.error("Error in getStaticProps: ", error);
    return {
      notFound: true,
      revalidate: parseInt(process.env.REVALIDATE_AFTER || "120"),
    };
  }
}
